import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link,
  ListSubheader,
  Divider,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material';
import type { ReactElement, ReactNode } from 'react';
import {
  useContext,
  useMemo,
  useState,
  createContext,
  useCallback
} from 'react';
import { SIDENAV_WIDTH } from '@natomalabs/natomui/src/theme/sidenav';
import { usePathname } from 'next/navigation';
import { MeContext } from '../providers/Me';
import ProfileButton from './ProfileButton';
import OrgSwitcher from './OrgSwitcher';
import {
  AgentIcon,
  AppsIcon,
  DiscoverIcon,
  DocumentationIcon,
  LineChartUpIcon,
  LockUnlockedIcon,
  MyAppsIcon,
  SettingsIcon,
  TunnelsIcon,
  UsersIcon,
  UserSquareIcon
} from '@natomalabs/natomui/src/icons';
import useIsFeatureSwitchEnabled from '../hooks/use_feature_switch';
import type { FeatureSwitchKey } from '@natomalabs/common/feature_switches';
import { BillingFeatureId, UserRole } from '../../__generated__/graphql';
import useIsBillingFeatureEnabled from '../hooks/use_billing_feature';
import {
  AgentsPath,
  AppsPath,
  MyAgentsPath,
  MyConnectionsPath,
  ProfilesPath,
  DirectoryPath,
  AccessPath,
  ActivityPath,
  ShadowAIPath,
  TunnelsPath
} from '@/src/lib/paths';
import {
  GlobalSidebarTabs,
  MCPSidebarTabs,
  type SidebarTabs
} from '@natomalabs/common/sidebar';
import { rolePermissions } from '@natomalabs/common/users';

// Context to track which sidebar item is currently loading
interface LoadingContextType {
  loadingItemId: string | null;
  setLoadingItemId: (id: string | null) => void;
  clearLoadingState: () => void;
}

const LoadingContext = createContext<LoadingContextType | null>(null);

const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [loadingItemId, setLoadingItemId] = useState<string | null>(null);

  const clearLoadingState = useCallback(() => {
    setLoadingItemId(null);
  }, []);

  return (
    <LoadingContext.Provider
      value={{ loadingItemId, setLoadingItemId, clearLoadingState }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

// Export LoadingProvider for use in other components
export { LoadingProvider };

// Custom hook to clear loading state from outside the sidebar
export const useClearSidebarLoading = () => {
  const loadingContext = useContext(LoadingContext);
  return loadingContext?.clearLoadingState;
};

interface ISidebarItem {
  id: SidebarTabs;
  label: string;
  href: string;
  hidden?: boolean;
  // Recursively nested sidebar tabs
  nested?: ISidebarItem[];
  icon: ReactElement;
  // If feature switch not enabled, show coming soon
  featureSwitch?: FeatureSwitchKey;
  // If true, show the upgrade treatment
  billingFeature?: BillingFeatureId;
  // Custom click handler for special cases
  onClick?: (e: React.MouseEvent) => void;
}
interface ISidebarSection {
  title?: string;
  items: ISidebarItem[];
  billingFeature?: BillingFeatureId;
  userRole?: UserRole;
}

const AIConnectSection: ISidebarSection = {
  title: 'Connect',
  items: [
    {
      id: MCPSidebarTabs.MY_CONNECTIONS,
      label: 'My Connections',
      href: MyConnectionsPath,
      icon: <MyAppsIcon size="medium" />
    },
    {
      id: MCPSidebarTabs.MY_AGENTS,
      label: 'My Agents',
      href: MyAgentsPath,
      icon: <AgentIcon size="medium" />,
      featureSwitch: 'showMyAgentsTab',
      billingFeature: BillingFeatureId.AgentIdentity
    }
  ],
  billingFeature: BillingFeatureId.AiIdentity
};

/**
 * Returns the role-specific documentation URL for a given user role.
 * @param role - The user's role
 * @returns The documentation URL specific to that role
 */
const getDocumentationUrl = (role: UserRole | null | undefined): string => {
  switch (role) {
    case UserRole.Admin:
      return 'https://docs.natoma.ai/role-guides/natoma-for-admins';
    case UserRole.AppAdmin:
      return 'https://docs.natoma.ai/role-guides/natoma-for-app-admins';
    case UserRole.Member:
      return 'https://docs.natoma.ai/role-guides/natoma-for-members';
    default:
      return 'https://docs.natoma.ai';
  }
};

// Create a dynamic Manage section to handle feature flag dependent menu items
const createAIManageSection = (): ISidebarSection => {
  return {
    title: 'Manage',
    items: [
      {
        id: MCPSidebarTabs.SHADOW_AI,
        label: 'Discovery',
        href: ShadowAIPath,
        billingFeature: BillingFeatureId.DiscoverAi,
        icon: <DiscoverIcon size="medium" />
      },
      {
        id: MCPSidebarTabs.APPS,
        label: 'Apps',
        href: AppsPath,
        icon: <AppsIcon size="medium" />
      },
      {
        id: MCPSidebarTabs.PROFILES,
        label: 'Profiles',
        href: ProfilesPath,
        billingFeature: BillingFeatureId.Profiles,
        icon: <UserSquareIcon size="medium" />
      },
      {
        id: MCPSidebarTabs.ACCESS,
        label: 'Access',
        href: AccessPath,
        billingFeature: BillingFeatureId.AccessPolicies,
        icon: <LockUnlockedIcon size="medium" />
      },
      {
        id: MCPSidebarTabs.ACTIVITY,
        label: 'Activity',
        href: ActivityPath,
        icon: <LineChartUpIcon size="medium" />,
        billingFeature: BillingFeatureId.AiActivityMonitoring
      },
      {
        id: MCPSidebarTabs.AGENTS,
        label: 'Agents',
        href: AgentsPath,
        billingFeature: BillingFeatureId.AgentIdentity,
        icon: <AgentIcon size="medium" />
      },
      {
        id: MCPSidebarTabs.DIRECTORY,
        label: 'Directory',
        href: DirectoryPath,
        icon: <UsersIcon size="medium" />
      },
      {
        id: MCPSidebarTabs.TUNNELS,
        label: 'Tunnels',
        href: TunnelsPath,
        featureSwitch: 'tunnels',
        billingFeature: BillingFeatureId.OnPremiseServers,
        icon: <TunnelsIcon size="medium" />
      }
    ],
    billingFeature: BillingFeatureId.AiIdentity,
    userRole: UserRole.Admin
  };
};

/**
 * Helper component for one sidebar button.
 * Renders recursively if the item is nested.
 *
 * @param item SideBarItem metadata to render
 * @returns React.JSX.Element that represents one sidebar button
 */
const SidebarLayoutButton = ({
  item,
  showNested,
  isNested
}: {
  item: ISidebarItem;
  showNested: boolean;
  isNested?: boolean;
}) => {
  const isFeatureSwitchEnabled = useIsFeatureSwitchEnabled();
  const isBillingFeatureEnabled = useIsBillingFeatureEnabled();
  const pathname = usePathname();
  const loadingContext = useContext(LoadingContext);

  const isLoading = loadingContext?.loadingItemId === item.id;

  // determine if this is an active button
  const isActive = useMemo(() => {
    // If this button is loading, it should be active
    if (isLoading) {
      return true;
    }

    // If any button is loading, only the loading button should be active
    if (loadingContext?.loadingItemId) {
      return false;
    }

    const pathValue = `/${pathname.split('/')[1]}`;
    return (
      item.href.startsWith(pathValue) ||
      (item.nested && item.nested.some((v) => v.href.startsWith(pathValue)))
    );
  }, [item, pathname, isLoading, loadingContext?.loadingItemId]);

  const [disabled, chip] = useMemo<[boolean, ReactNode | null]>(() => {
    let chip: ReactNode | null = null;
    let disabled = false;
    if (item.billingFeature && !isBillingFeatureEnabled(item.billingFeature)) {
      chip = (
        <Chip label="Upgrade" sx={{ px: 1, py: '2px', borderRadius: '6px' }} />
      );
      disabled = true;
    } else if (
      item.featureSwitch &&
      !isFeatureSwitchEnabled(item.featureSwitch)
    ) {
      chip = (
        <Chip label="Soon" sx={{ px: 1, py: '2px', borderRadius: '6px' }} />
      );
      disabled = true;
    }
    return [disabled, chip];
  }, [item, isFeatureSwitchEnabled, isBillingFeatureEnabled]);

  const handleClick = (e: React.MouseEvent) => {
    // Handle custom click handler first
    if (item.onClick) {
      item.onClick(e);
      return;
    }

    // Don't show loading if this button is already active
    if (isActive) {
      return;
    }

    // Only show loading for internal links
    if (!item.href.startsWith('http') && loadingContext) {
      loadingContext.setLoadingItemId(item.id);
    }
  };

  return (
    <>
      <Tooltip
        placement="right"
        title={
          disabled
            ? 'This feature is only included in paid editions of Natoma'
            : undefined
        }
      >
        <ListItemButton
          component={Link}
          disabled={disabled}
          key={item.id}
          href={item.href}
          data-active={isActive}
          onClick={handleClick}
          target={item.href.startsWith('http') ? '_blank' : '_self'}
          sx={(theme) => ({
            '&.MuiListItemButton-root': {
              m: 0,
              height: '28px',
              minHeight: '28px',
              borderRadius: '6px',
              ...(isNested && { borderRadius: theme.spacing(1.5) })
            },
            columnGap: 1,
            '&[data-active="true"]': {
              backgroundColor: theme.palette.sidenav.activeBackground,
              color: theme.palette.sidenav.activeForeground,
              cursor: 'default',
              '&:hover': {
                backgroundColor: theme.palette.sidenav.activeBackgroundHover,
                color: theme.palette.sidenav.activeForeground
              },
              '& .MuiListItemIcon-root': {
                color: theme.palette.sidenav.activeForeground
              }
            },
            ...(isNested && { height: '32px' })
          })}
        >
          <ListItemIcon>
            {isLoading ? (
              <CircularProgress
                size={20}
                color="inherit"
                sx={{ color: 'inherit', flexShrink: 0 }}
              />
            ) : (
              item.icon
            )}
          </ListItemIcon>
          <ListItemText primary={item.label} />
          {chip}
        </ListItemButton>
      </Tooltip>
      {showNested && item.nested && item.nested.length > 0 && (
        <List
          data-active={isActive || isLoading}
          disablePadding
          component="div"
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            ml: 3,
            pl: 1,
            borderLeft: '2px solid',
            borderColor: theme.palette.divider,
            '&[data-active="true"]': {
              borderColor: theme.palette.primary.main
            }
          })}
        >
          {item.nested.map((nestedItem) => {
            const hidden = nestedItem.featureSwitch
              ? !isFeatureSwitchEnabled(nestedItem.featureSwitch)
              : nestedItem.hidden;
            return (
              !hidden && (
                <SidebarLayoutButton
                  key={nestedItem.id}
                  item={nestedItem}
                  showNested={showNested}
                  isNested
                />
              )
            );
          })}
        </List>
      )}
    </>
  );
};

export const testIds = {
  sidebar: 'sidebar',
  sidebarOrgSwitcher: 'sidebar-org-switcher',
  sidebarItems: 'sidebar-items'
};

function SidebarItemsSection({ section }: { section: ISidebarSection }) {
  const { currentUser } = useContext(MeContext);
  const isBillingFeatureEnabled = useIsBillingFeatureEnabled();
  const isFeatureSwitchEnabled = useIsFeatureSwitchEnabled();

  // determine if we should show the section
  const showSection = useMemo(() => {
    return (
      !section.billingFeature || isBillingFeatureEnabled(section.billingFeature)
    );
  }, [section.billingFeature, isBillingFeatureEnabled]);

  const visibleItems = useMemo(() => {
    return section.items.filter((item) => {
      const hidden =
        (item.featureSwitch
          ? !isFeatureSwitchEnabled(item.featureSwitch)
          : item.hidden) ||
        // hide the tabs the org doesnt have access to
        (item.billingFeature && !isBillingFeatureEnabled(item.billingFeature));

      // we want to gate sidebar nav tabs to specific roles
      const roleOk = rolePermissions[
        currentUser?.role || UserRole.Member
      ].sidebarTabs.includes(item.id);

      return !hidden && roleOk;
    });
  }, [
    section.items,
    currentUser,
    isFeatureSwitchEnabled,
    isBillingFeatureEnabled
  ]);

  if (!showSection || visibleItems.length === 0) {
    return null;
  }

  return (
    <>
      {section.title && (
        <ListSubheader disableSticky>{section.title}</ListSubheader>
      )}
      {visibleItems.map((item) => {
        return <SidebarLayoutButton key={item.id} item={item} showNested />;
      })}
    </>
  );
}

export const SIDEBAR_PX_WIDTH = SIDENAV_WIDTH;

// wraps the children in a parent List component with special style
export function SidebarList({ children }: { children: ReactNode }) {
  return (
    <List
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
        maxWidth: 360,
        color: theme.palette.sidenav.defaultForeground,
        padding: theme.spacing(0),
        '& .MuiListSubheader-root': {
          backgroundColor: 'transparent',
          fontSize: '12px',
          fontWeight: 700,
          textTransform: 'uppercase'
        },
        '& .MuiListItemButton-root': {
          borderRadius: '6px',
          margin: '1px'
        },
        '& .MuiListItemButton-root:hover': {
          backgroundColor: theme.palette.sidenav.hoverBackground,
          color: theme.palette.sidenav.defaultForeground
        },
        '& .MuiListItemIcon-root': {
          minWidth: theme.spacing(0),
          color: theme.palette.sidenav.defaultForeground
        }
      })}
      component="nav"
    >
      {children}
    </List>
  );
}

export default function SidebarLayout({ children }: { children: ReactNode }) {
  const { isAuthenticated, isAuthorized, currentUser } = useContext(MeContext);

  // Create the dynamic AIManageSection
  const AIManageSection = useMemo(() => createAIManageSection(), []);

  // Create the dynamic GlobalSidebarSection
  const GlobalSidebarSection = useMemo((): ISidebarSection => {
    const items: ISidebarItem[] = [
      {
        id: GlobalSidebarTabs.DOCUMENTATION,
        label: 'Documentation',
        href: getDocumentationUrl(currentUser?.role),
        icon: <DocumentationIcon size="medium" />
      }
    ];

    items.push({
      id: GlobalSidebarTabs.SETTINGS,
      label: 'Admin',
      href: '/settings/general',
      icon: <SettingsIcon size="medium" />
    });

    return {
      items
    };
  }, [currentUser?.role]);

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'row',
        maxWidth: '100%',
        minHeight: '100%',
        backgroundColor: theme.palette.background.default
      })}
    >
      {/* sidebar */}
      {isAuthenticated && (
        <Box
          component="aside"
          aria-label="Primary navigation"
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            width: SIDEBAR_PX_WIDTH,
            minWidth: SIDEBAR_PX_WIDTH,
            maxWidth: SIDEBAR_PX_WIDTH,
            flexShrink: 0,
            boxSizing: 'border-box',
            height: '100vh',
            px: 2,
            py: 2,
            backgroundColor: theme.palette.background.default,
            borderRight: `1px solid ${theme.palette.divider}`,
            justifyContent: 'space-between',
            overflowY: 'auto',
            gap: 2
          })}
          data-testid={testIds.sidebar}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: 2,
              flex: 1
            }}
          >
            <OrgSwitcher />
            {isAuthorized && (
              <Box data-testid={testIds.sidebarItems}>
                <SidebarList>
                  <SidebarItemsSection section={AIConnectSection} />
                  <SidebarItemsSection section={AIManageSection} />
                </SidebarList>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 1.5 }}>
            {isAuthorized && (
              <SidebarList>
                <SidebarItemsSection section={GlobalSidebarSection} />
              </SidebarList>
            )}
          </Box>
          <Divider />
          <Box>
            <ProfileButton />
          </Box>
        </Box>
      )}
      {/* main content — page background, no floating paper shell */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          [`@media (max-width: 1000px)`]: {
            overflow: 'auto'
          }
        }}
        position="relative"
      >
        <Box
          sx={{
            width: '100%',
            height: '100vh',
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={(theme) => ({
              flexGrow: 1,
              minWidth: '1000px',
              overflow: 'auto',
              [theme.breakpoints.up('xl')]: {
                padding: theme.spacing(3)
              },
              [theme.breakpoints.down('xl')]: {
                padding: theme.spacing(2)
              },
              bgcolor: theme.palette.background.default
            })}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
